import { useTagCustomOrderByGrouping } from 'features/tags/hooks/use-tag-custom-order-by-grouping';
import { useUpdateTag } from 'features/tags/hooks/use-update-tag';
import { useUpdateTasks } from 'features/tasks';
import React, { useCallback, useMemo } from 'react';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { DocumentOrganizationKeys } from 'shared/types/filter-document';
import { ID } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { GroupByMode, SortingMode } from 'shared/types/sorting-mode';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { defaultFilter, getFilterDocument } from '../../constants';

type TasksOverviewProps = {
  scrollElement?: React.RefObject<HTMLDivElement>;
  tag: Tag;
};

export const TasksOverview: React.FC<TasksOverviewProps> = ({
  scrollElement,
  tag,
}) => {
  const { submit: updateTag } = useUpdateTag();
  const { submit: updateTasks } = useUpdateTasks();
  const updateRelationships = useUpdateRelationshipsReferences();

  const filterDocument = useMemo(() => getFilterDocument(tag), [tag]);

  const groupSettings = useMemo(
    () => ({
      [GroupByMode.Section]: { tagId: tag.id },
      [GroupByMode.Goal]: { tagId: tag.id },
      [GroupByMode.LifeArea]: { tagId: tag.id },
      [GroupByMode.DayPart]: { tagId: tag.id },
      [GroupByMode.Priority]: { tagId: tag.id },
      [GroupByMode.Type]: { tagId: tag.id },
      [GroupByMode.Deadline]: { tagId: tag.id },
      [GroupByMode.DeadlineDeadline]: { tagId: tag.id },
      [GroupByMode.Tag]: { tagId: tag.id },
      [GroupByMode.None]: { tagId: tag.id },
    }),
    [tag.id],
  );

  const onSectionCollapse = useCallback(
    (collapsedSections: ID[]) => {
      if (!tag) {
        return;
      }

      updateTag({
        ...tag,
        filterDocument: {
          ...(tag.filterDocument ?? {}),
          collapsedSections,
        },
      });
    },
    [tag, updateTag],
  );

  const onReset = () =>
    updateTag({
      ...tag,
      filterDocument: {
        ...defaultFilter(tag),
        collapsedSections: tag.filterDocument?.collapsedSections ?? null,
      },
    });

  const onReorder = useCallback(
    (
      updatedSections: { id: ID; taskSorting: ID[] }[],
      updatedTasks: Task[],
      relationshipUpdates?: RelationshipUpdate[],
    ) => {
      if (!tag) {
        return;
      }

      // Handle task updates (e.g. reparenting/move)
      updateTasks(updatedTasks);
      const groupBy =
        filterDocument[DocumentOrganizationKeys.GroupByMode] ??
        GroupByMode.Priority;

      // Handling "No Grouping" (or what was Section in LifeArea)
      if (groupBy === GroupByMode.None) {
        // NoGroupingTasksList sends a single section with the new order.
        // We use the first one to avoid potential ID mismatches, and filter out 'new-task'.
        const newSorting = updatedSections[0]?.taskSorting?.filter(
          (id) => id !== 'new-task',
        );

        if (newSorting) {
          updateTag({
            ...tag,
            taskSorting: newSorting,
            filterDocument: {
              ...(tag.filterDocument ?? {}),
              sortingMode: SortingMode.Custom,
            },
          });
        }

        // handle relationship updates if provided
        if (relationshipUpdates?.length) {
          updateRelationships(relationshipUpdates);
        }
        return;
      }

      // handle sorting update for all groupings except 'noGrouping'
      const groupBySorting = updatedSections.reduce<Record<ID, ID[]>>(
        (acc, section) => {
          acc[section.id] = section.taskSorting;
          return acc;
        },
        {},
      );

      if (Object.keys(groupBySorting).length) {
        updateTag({
          ...tag,
          customTaskOrder: {
            ...(tag.customTaskOrder ?? {}),
            [groupBy]: groupBySorting,
          },
          filterDocument: {
            ...(tag.filterDocument ?? {}),
            sortingMode: SortingMode.Custom,
          },
        });
      }

      // handle relationship updates if provided
      if (relationshipUpdates?.length) {
        updateRelationships(relationshipUpdates);
      }
    },
    [filterDocument, tag, updateTag, updateRelationships, updateTasks],
  );

  const customOrder = useTagCustomOrderByGrouping(
    filterDocument.groupByMode,
    tag,
  );

  if (!tag) {
    return null;
  }

  return (
    <ProvidedTaskFilterList
      filterDocument={filterDocument}
      defaultFilterDocument={defaultFilter(tag)}
      customOrder={customOrder}
      groupSettings={groupSettings}
      onReorder={onReorder}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      onReset={onReset}
    />
  );
};
